import useCreateDetail, {
  UseCreateDetailOptions,
} from '@/service-library/core-hooks/useCreateDetail'
import useGet, { UseGetOptions } from '@/service-library/core-hooks/useGet'
import useGetList, {
  UseGetListOptions,
} from '@/service-library/core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '@/service-library/core-hooks/useUpdateDetail'
import { Filters } from '@/service-library/request-types'
import {
  createDetail,
  deleteWithBody,
  getDetail,
  getList,
  updateDetail,
} from '@/service-library/request-wrappers'
import { LiveListConfigs } from '@/types/live-list-filter'
import { LiveListEntity } from '@/types/live-lists'
import { PDPurchaseOrder } from '@/types/po-match'
import { BaseVendor, NewVendorBody } from '@/types/relay'
import {
  VendorSettings,
  VendorAlias,
  PaymentMethodWithCards,
} from '@/types/vendor-settings'
import useForeignKeyListEndpoint from '@/components/project-settings/params-schema-property-fields/useForeignKeyListEndpoint'

/**
 * MARK: Action Hook: Get Live List Data
 * Retrieves Live List data for a given info type ID, org ID, and org credential type ID.
 * Optionally accepts docId and docRowId for more specific queries.
 */
export function useGetLiveData<T>({
  infoTypeId,
  orgId,
  orgCredentialTypeId,
  docId,
  docRowId,
  enabled = true,
  ...options
}: Omit<UseGetListOptions<LiveListEntity<T>>, 'paginator'> & {
  infoTypeId: string
  orgId: string
  orgCredentialTypeId: string
  docId?: string
  docRowId?: string
}) {
  return useGetList<LiveListEntity<T>>({
    queryKey: [
      'live-data',
      options.filters,
      infoTypeId,
      orgId,
      orgCredentialTypeId,
      docId,
      docRowId,
    ],
    paginator: 'page',
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getList<LiveListEntity<T>>({
        url: '/v2/relay/live_data',
        ...serviceFnOptions,
        filters: {
          ...filters,
          info_type_id: infoTypeId,
          org_id: orgId,
          org_credential_type_id: orgCredentialTypeId,
          doc_id: docId,
          doc_row_id: docRowId,
        },
      })
    },
    enabled: !!(infoTypeId && orgId && orgCredentialTypeId && enabled),
    ...options,
  })
}

/**
 * MARK: Action Hook: Get Live List Filter Data
 * Fetches live list data given a pre-built URL and query params produced by
 * useLiveListFilterBuilder. The query is disabled when either argument is null.
 */
export function useGetLiveListFilterData<T>({
  liveListUrl,
  domainFilters,
  enabled = true,
  ...options
}: Omit<UseGetListOptions<LiveListEntity<T>>, 'paginator'> & {
  liveListUrl: string | null | undefined
  domainFilters: Filters | null
  enabled: boolean
}) {
  return useForeignKeyListEndpoint<LiveListEntity<T>>({
    url: liveListUrl ?? undefined,
    filters: domainFilters ?? undefined,
    paginator: 'page',
    enabled: !!liveListUrl && !!domainFilters && enabled,
    ...options,
  })
}

/**
 * MARK: Action Hook: Get Purchase Orders
 * Retrieves purchase orders for a given info type ID, org ID, and org credential type ID.
 * Optionally accepts filters for more specific queries.
 */
export function useGetPDPurchaseOrders({
  infoTypeId,
  orgId,
  orgCredentialTypeId,
  enabled = true,
  ...options
}: Omit<UseGetListOptions<PDPurchaseOrder>, 'paginator'> & {
  infoTypeId: string
  orgId: string
  orgCredentialTypeId: string
}) {
  const query = useGetList<PDPurchaseOrder>({
    queryKey: [
      'pd_purchase_orders',
      options.filters,
      infoTypeId,
      orgId,
      orgCredentialTypeId,
    ],
    paginator: 'page',
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getList<PDPurchaseOrder>({
        url: '/v2/relay/pd_purchase_orders',
        ...serviceFnOptions,
        filters: {
          ...filters,
          info_type_id: infoTypeId,
          org_id: orgId,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    enabled: !!(infoTypeId && orgId && orgCredentialTypeId && enabled),
    ...options,
  })

  return {
    pdPurchaseOrders: query.allData,
    ...query,
  }
}

/**
 * MARK: Action Hook: Get Vendor Settings
 * Retrieves vendor settings for a given vendor ID, organization ID, and org credential type ID.
 */
export function useGetVendorSettings({
  orgId,
  vendorId,
  orgCredentialTypeId,
  enabled = true,
  ...options
}: UseGetOptions<VendorSettings> & {
  orgId: string
  vendorId: string
  orgCredentialTypeId: string
}) {
  const query = useGet<VendorSettings>({
    queryKey: [
      'vendor_settings',
      options.filters,
      vendorId,
      orgId,
      orgCredentialTypeId,
    ],
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getDetail<VendorSettings>({
        url: `/v2/relay/vendor_settings/${orgId}/${vendorId}`,
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    enabled: !!orgId && !!vendorId && !!orgCredentialTypeId && enabled,
    ...options,
  })

  return {
    vendorSettings: query.data,
    ...query,
  }
}

/**
 * MARK: Action Hook: Update Vendor Settings
 * Updates the vendor settings for a vendor.
 */
export function useUpdateVendorSettings({
  orgId,
  vendorId,
  orgCredentialTypeId,
  ...options
}: UseUpdateDetailOptions<VendorSettings & { id: string }> & {
  orgId: string
  vendorId: string
  orgCredentialTypeId: string
}) {
  const mutation = useUpdateDetail<VendorSettings & { id: string }>({
    serviceFn: ({ item, ...serviceFnOptions }) => {
      return updateDetail<VendorSettings & { id: string }>({
        url: `/v2/relay/vendor_settings/${orgId}/${vendorId}`,
        item,
        ...serviceFnOptions,
        filters: {
          ...serviceFnOptions.filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    ...options,
  })

  return {
    updateVendorSettings: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Vendor Alias
 * Creates a new vendor alias for a vendor.
 */
export function useCreateVendorAlias({
  orgCredentialTypeId,
  ...options
}: Omit<UseCreateDetailOptions<VendorAlias>, 'listQueryKey'> & {
  orgCredentialTypeId: string
}) {
  const mutation = useCreateDetail<VendorAlias>({
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return createDetail<VendorAlias>({
        url: '/v2/relay/vendor_aliases',
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    ...options,
  })

  return {
    createVendorAlias: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Delete Vendor Alias
 * Deletes a vendor alias for a vendor.
 */
export function useDeleteVendorAlias({
  orgCredentialTypeId,
  ...options
}: Omit<UseCreateDetailOptions<VendorAlias, void>, 'listQueryKey'> & {
  orgCredentialTypeId: string
}) {
  const mutation = useCreateDetail<VendorAlias, void>({
    // using useCreateDetail since it expects an object instead of just an ID
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return deleteWithBody({
        url: '/v2/relay/vendor_aliases',
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    ...options,
  })

  return {
    deleteVendorAlias: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get Payment Methods with Cards
 * Retrieves payment methods with card details for a given payment method ID, org ID, and org credential type ID.
 */
export function useGetPaymentMethodWithCards({
  orgId,
  paymentMethodId,
  orgCredentialTypeId,
  enabled = true,
  ...options
}: UseGetOptions<PaymentMethodWithCards> & {
  orgId: string
  paymentMethodId: string
  orgCredentialTypeId: string
}) {
  const query = useGet<PaymentMethodWithCards>({
    queryKey: [
      'payment_method_with_cards',
      options.filters,
      paymentMethodId,
      orgId,
      orgCredentialTypeId,
    ],
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return getDetail<PaymentMethodWithCards>({
        url: `/v2/relay/payment_methods/${orgId}/${paymentMethodId}`,
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    enabled: !!(orgId && paymentMethodId && orgCredentialTypeId && enabled),
    ...options,
  })

  return {
    paymentMethodWithCards: query.data,
    ...query,
  }
}

/**
 * MARK: Action Hook: Update Payment Method with Cards
 * Updates the payment method with card details.
 */
export function useUpdatePaymentMethodWithCards({
  orgCredentialTypeId,
  ...options
}: UseUpdateDetailOptions<PaymentMethodWithCards> & {
  orgCredentialTypeId: string
}) {
  const mutation = useUpdateDetail<PaymentMethodWithCards>({
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return updateDetail<PaymentMethodWithCards>({
        url: '/v2/relay/payment_methods',
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    ...options,
  })

  return {
    updatePaymentMethodWithCards: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Vendor
 * Creates a new vendor for a given organization and org credential type.
 */
export function useCreateVendor<T extends BaseVendor>({
  orgCredentialTypeId,
  orgId,
  ...options
}: UseCreateDetailOptions<NewVendorBody, LiveListEntity<T>> & {
  orgCredentialTypeId: string
  orgId: string
}) {
  const mutation = useCreateDetail<NewVendorBody, LiveListEntity<T>>({
    serviceFn: ({ filters, ...serviceFnOptions }) => {
      return createDetail<NewVendorBody, LiveListEntity<T>>({
        url: `/v2/relay/${orgId}/vendors`,
        ...serviceFnOptions,
        filters: {
          ...filters,
          org_credential_type_id: orgCredentialTypeId,
        },
      })
    },
    ...options,
  })

  return {
    createVendor: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get LiveListConfigs
 * Retrieves live list configurations for a given org credential type. The
 * resolved config bundle can vary per CredentialType, so it's cached per
 * orgCredentialTypeId.
 */
export function useGetLiveListConfigs({
  orgCredentialTypeId,
  enabled = true,
  ...options
}: Omit<UseGetOptions<LiveListConfigs>, 'filters'> & {
  orgCredentialTypeId: string
}) {
  const query = useGet<LiveListConfigs>({
    queryKey: ['relay', 'live-list-configs', orgCredentialTypeId],
    serviceFn: (serviceFnOptions) =>
      getDetail<LiveListConfigs>({
        url: '/v2/relay/live_data/config',
        ...serviceFnOptions,
        filters: { org_credential_type_id: orgCredentialTypeId },
      }),
    // configs are essentially static for the session
    staleTime: 5 * 60 * 1000,
    enabled: !!orgCredentialTypeId && enabled,
    ...options,
  })

  return {
    liveListConfigs: query.data,
    ...query,
  }
}
